import { useCallback } from "react";

import { produce } from "immer";

import { ErrorKind, useErrorCallback } from "@/data/error/useError";
import {
  TaskDocument,
  TaskQuery,
  useSwapResourceMutation,
} from "@/generated/graphql";

// Callback to move the first resource to the position of second resource (swap).
type OnMoveResource = (dragResId: string, hoverResId: string) => void;
type CallbackTuple = [OnMoveResource, boolean];

export function useMoveResource(
  assessmentId: string,
  taskId: string
): CallbackTuple {
  const onError = useErrorCallback(ErrorKind.EditTask);

  const [mutate, { loading }] = useSwapResourceMutation({
    ignoreResults: true,
    onError,
  });

  const onMoveResource = useCallback<OnMoveResource>(
    (dragResId, hoverResId) => {
      mutate({
        variables: {
          id: dragResId,
          swapId: hoverResId,
        },
        optimisticResponse: {
          swapResource: true,
        },
        update: (proxy) => {
          const cacheData = proxy.readQuery<TaskQuery>({
            query: TaskDocument,
            variables: { assessmentId, taskId },
          });

          if (cacheData) {
            const newData = produce(cacheData, (draft) => {
              if (!draft.task) return;

              const dragged = draft.task.resources.findIndex(
                (r) => r.id === dragResId
              );
              const hovered = draft.task.resources.findIndex(
                (r) => r.id === hoverResId
              );

              if (dragged !== -1 && hovered !== -1) {
                const temp = draft.task.resources[dragged]!;
                draft.task.resources[dragged] = draft.task.resources[hovered]!;
                draft.task.resources[hovered] = temp;
              }
            });

            proxy.writeQuery<TaskQuery>({
              query: TaskDocument,
              variables: { assessmentId, taskId },
              data: newData,
            });
          }
        },
      });
    },
    [mutate, assessmentId, taskId]
  );

  return [onMoveResource, loading];
}
